import React from 'react';
import { TODAY, df } from '../utils/dates';

/**
 * ShiftMode — a minimal focused view for shift workers
 * Shows: next shift, today tasks, last workout, big mic button
 * Activated via Settings or the mode toggle in the header
 */
function ShiftMode({ st, setTab, onVoice, setSt, notify }) {
  const name     = st.profile?.name?.split(' ')[0] || '';
  const hour     = new Date().getHours();
  const greeting = hour < 5 ? 'Still up' : hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening';

  // Next shift from calendar
  const shifts = (st.events||[])
    .filter(e => /shift|work/i.test(e.type||e.title||'') && e.date >= TODAY)
    .sort((a,b) => a.date.localeCompare(b.date) || (a.time||'').localeCompare(b.time||''));
  const nextShift = shifts[0];

  // Today's tasks
  const todayTasks = (st.tasks||[]).filter(t => !t.done && (t.bucket==='today'||t.dueDate===TODAY));

  // Last workout
  const lastWorkout = (st.workouts||[]).sort((a,b)=>b.date.localeCompare(a.date))[0];
  const daysSince = lastWorkout ? -df(lastWorkout.date) : null;

  // Payday
  const nextPay   = (st.income||[]).filter(i=>i.nextDate).sort((a,b)=>new Date(a.nextDate)-new Date(b.nextDate))[0];
  const daysTopay = nextPay ? df(nextPay.nextDate) : null;

  const toggleTask = (id) => {
    setSt(p => ({
      ...p,
      tasks: (p.tasks||[]).map(t => t.id === id ? {...t, done: !t.done} : t)
    }));
  };

  return (
    <div className="shift-wrap">
      {/* Header */}
      <div className="shift-header">
        <div>
          <div className="shift-greeting">{greeting}{name ? `, ${name}` : ''}.</div>
          <div className="shift-date">{new Date().toLocaleDateString('en-US',{weekday:'long',month:'short',day:'numeric'})}</div>
        </div>
        <button className="shift-exit-btn" onClick={() => setSt(p=>({...p,prefs:{...p.prefs,shiftMode:false}}))} title="Exit shift mode">
          ✕
        </button>
      </div>

      {/* Next shift */}
      <div className="shift-card shift-card-primary">
        <div className="shift-card-label">NEXT SHIFT</div>
        {nextShift ? (
          <div className="shift-card-main">
            <div className="shift-card-title">{nextShift.title}</div>
            <div className="shift-card-sub">
              {nextShift.date === TODAY ? 'Today' : df(nextShift.date) === 1 ? 'Tomorrow' : nextShift.date}
              {nextShift.time ? ` at ${nextShift.time}` : ''}
            </div>
          </div>
        ) : (
          <div className="shift-card-empty" onClick={() => setTab('calendar')}>
            No shifts scheduled — tap to add →
          </div>
        )}
      </div>

      {/* Tasks */}
      <div className="shift-card">
        <div className="shift-card-label">TODAY  <span className="shift-card-count">{todayTasks.length}</span></div>
        {todayTasks.length > 0 ? (
          <div className="shift-tasks">
            {todayTasks.slice(0, 5).map(t => (
              <div key={t.id} className="shift-task" onClick={() => toggleTask(t.id)}>
                <div className="shift-task-check" />
                <span className="shift-task-text">{t.text}</span>
              </div>
            ))}
            {todayTasks.length > 5 && (
              <div className="shift-task-more" onClick={() => setTab('tasks')}>
                +{todayTasks.length - 5} more →
              </div>
            )}
          </div>
        ) : (
          <div className="shift-card-empty" onClick={() => setTab('tasks')}>All clear — tap to add tasks →</div>
        )}
      </div>

      {/* Stats row */}
      <div className="shift-stats">
        <div className="shift-stat" onClick={() => setTab('health')}>
          <div className="shift-stat-val">
            {daysSince === null ? '—' : daysSince === 0 ? 'Today' : `${daysSince}d`}
          </div>
          <div className="shift-stat-label">Last workout</div>
        </div>
        <div className="shift-stat" onClick={() => setTab('finance')}>
          <div className="shift-stat-val" style={{color: daysTopay !== null && daysTopay <= 2 ? 'var(--grn)' : 'var(--t2)'}}>
            {daysTopay === null ? '—' : daysTopay === 0 ? 'Today' : daysTopay === 1 ? 'Tmrw' : `${daysTopay}d`}
          </div>
          <div className="shift-stat-label">Payday</div>
        </div>
        <div className="shift-stat" onClick={() => setTab('ask')}>
          <div className="shift-stat-val">✦</div>
          <div className="shift-stat-label">Ask Mylo</div>
        </div>
      </div>

      {/* Big voice button */}
      <button className="shift-voice-btn" onClick={onVoice}>
        <span className="shift-voice-icon">◎</span>
        <span className="shift-voice-label">Tap to speak</span>
      </button>

      <div className="shift-footer">
        Log a workout · Add a task · Ask anything
      </div>
    </div>
  );
}

export default ShiftMode;
